import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { syncSearchParams } from '../lib/urlSync';
import { getCached, swrFetch, invalidatePrefix } from '../lib/dataCache';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { Switch } from '../components/ui/switch';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { toast } from 'sonner';
import { useAuth } from '../lib/auth';
import { calendarAPI, prospectsAPI, tasksAPI, usersAPI, googleCalendarAPI } from '../lib/api';
import { INTERNAL_THEMES } from '../lib/internalThemes';
import { Calendar as CalendarIcon, Plus, ChevronLeft, ChevronRight, Clock, MapPin, Loader2, Trash2, Edit, List, Search, Check, Building2, CheckSquare, Square, Users, UsersRound, ChevronDown, X, UserPlus, RefreshCw } from 'lucide-react';
import { cn } from '../lib/utils';

const TASK_PRIORITY_COLORS = {
    urgent: '#EF4444',
    high: '#F97316',
    medium: '#8B5CF6',
    low: '#6B7280'
};

const WEEKDAYS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const WEEKDAYS_SHORT = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];

const COLORS = [
    { value: '#3B82F6', name: 'Bleu' },
    { value: '#10B981', name: 'Vert' },
    { value: '#F59E0B', name: 'Orange' },
    { value: '#EF4444', name: 'Rouge' },
    { value: '#8B5CF6', name: 'Violet' },
    { value: '#EC4899', name: 'Rose' },
    { value: '#6366F1', name: 'Indigo' },
];

// Time slots every 30 min
const TIME_SLOTS = [];
for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
        TIME_SLOTS.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    }
}

function formatDateFr(dateStr) {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

function formatDateShort(dateStr) {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'short' });
}

function formatTime(dateStr) {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

function getMonthLabel(dt) {
    return dt.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
}

function buildMonthGrid(year, month) {
    const result = [];
    const firstOfMonth = new Date(year, month, 1);
    const lastOfMonth = new Date(year, month + 1, 0);
    let startDay = firstOfMonth.getDay() - 1;
    if (startDay < 0) startDay = 6;
    const prevMonthLast = new Date(year, month, 0).getDate();
    for (let i = startDay - 1; i >= 0; i--) {
        result.push({ day: prevMonthLast - i, cur: false, dt: new Date(year, month - 1, prevMonthLast - i) });
    }
    for (let d = 1; d <= lastOfMonth.getDate(); d++) {
        result.push({ day: d, cur: true, dt: new Date(year, month, d) });
    }
    while (result.length < 42) {
        const nextDay = result.length - startDay - lastOfMonth.getDate() + 1;
        result.push({ day: nextDay, cur: false, dt: new Date(year, month + 1, nextDay) });
    }
    return result;
}

function dateToYMD(dt) {
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, '0');
    const d = String(dt.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}

function sameDay(a, b) {
    return a.getDate() === b.getDate() && a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear();
}

function combineDateAndTime(dateStr, timeStr) {
    if (!dateStr || !timeStr) return '';
    return `${dateStr}T${timeStr}:00`;
}

function extractDate(datetimeStr) {
    if (!datetimeStr) return '';
    return datetimeStr.split('T')[0];
}

function extractTime(datetimeStr) {
    if (!datetimeStr) return '09:00';
    const parts = datetimeStr.split('T');
    if (parts.length > 1) {
        return parts[1].substring(0, 5);
    }
    return '09:00';
}

function addHoursToTime(timeStr, hours) {
    const [h, m] = timeStr.split(':').map(Number);
    const newH = Math.min(23, h + hours);
    return `${String(newH).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

// Mini calendar for the form
function MiniCalendar({ selectedDate, onSelect, month, onMonthChange }) {
    const grid = buildMonthGrid(month.getFullYear(), month.getMonth());
    const today = new Date();
    
    return (
        <div className="w-full">
            <div className="flex items-center justify-between mb-2">
                <button type="button" onClick={() => onMonthChange(new Date(month.getFullYear(), month.getMonth() - 1, 1))} className="p-1 hover:bg-slate-100 rounded">
                    <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-sm font-medium capitalize">{month.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}</span>
                <button type="button" onClick={() => onMonthChange(new Date(month.getFullYear(), month.getMonth() + 1, 1))} className="p-1 hover:bg-slate-100 rounded">
                    <ChevronRight className="w-4 h-4" />
                </button>
            </div>
            <div className="grid grid-cols-7 gap-0 text-center">
                {WEEKDAYS_SHORT.map(d => (
                    <div key={d} className="text-xs text-slate-500 py-1">{d}</div>
                ))}
                {grid.map((g, i) => {
                    const isSelected = selectedDate && sameDay(g.dt, new Date(selectedDate));
                    const isToday = sameDay(g.dt, today);
                    return (
                        <button
                            key={i}
                            type="button"
                            onClick={() => onSelect(dateToYMD(g.dt))}
                            className={cn(
                                "w-8 h-8 text-sm rounded-full transition-colors",
                                !g.cur && "text-slate-300",
                                g.cur && !isSelected && "hover:bg-slate-100",
                                isToday && !isSelected && "text-blue-600 font-semibold",
                                isSelected && "bg-blue-600 text-white"
                            )}
                        >
                            {g.day}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

export default function CalendarPage() {
    const { user } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const [evts, setEvts] = useState([]);
    const [tasks, setTasks] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [curDate, setCurDate] = useState(searchParams.get('date') ? new Date(searchParams.get('date') + 'T00:00:00') : new Date());
    const [view, setView] = useState(searchParams.get('view') || 'month');
    const [dlgOpen, setDlgOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);
    const [selected, setSelected] = useState(null);
    const [selectedTask, setSelectedTask] = useState(null);
    const [prospects, setProspects] = useState([]);
    const [prospectSearch, setProspectSearch] = useState('');
    const [prospectPopoverOpen, setProspectPopoverOpen] = useState(false);
    const [showTasks, setShowTasks] = useState(searchParams.get('tasks') !== '0');
    const [showEvents, setShowEvents] = useState(searchParams.get('events') !== '0');
    const [filterScope, setFilterScope] = useState(searchParams.get('scope') || 'mine');
    const [filterMember, setFilterMember] = useState(searchParams.get('member') || '');
    const [themeFilter, setThemeFilter] = useState(searchParams.get('theme') || 'all');
    const [teamMembers, setTeamMembers] = useState([]);
    const [showOptions, setShowOptions] = useState(false);
    const [formMonth, setFormMonth] = useState(new Date());

    // === Google Agenda (connexion + synchro) ===
    const [gcalState, setGcalState] = useState({ loading: true, connected: false, email: null });
    const [gcalSyncing, setGcalSyncing] = useState(false);

    const loadGcalStatus = useCallback(async () => {
        try {
            const res = await googleCalendarAPI.status();
            setGcalState({ loading: false, connected: !!res.data?.connected, email: res.data?.email || null });
        } catch {
            setGcalState({ loading: false, connected: false, email: null });
        }
    }, []);

    const handleGcalConnect = async () => {
        try {
            const res = await googleCalendarAPI.login(window.location.origin);
            const url = res.data?.authorization_url;
            if (url) window.location.href = url;
        } catch {
            toast.error("Impossible de démarrer la connexion Google Agenda.");
        }
    };

    const handleGcalDisconnect = async () => {
        try {
            await googleCalendarAPI.disconnect();
            toast.success("Google Agenda déconnecté.");
            setGcalState({ loading: false, connected: false, email: null });
        } catch {
            toast.error("Échec de la déconnexion.");
        }
    };

    const handleGcalSync = async () => {
        setGcalSyncing(true);
        try {
            const res = await googleCalendarAPI.sync();
            const { pushed = 0, imported = 0 } = res.data || {};
            toast.success(`Synchronisé : ${pushed} envoyé(s) vers Google, ${imported} importé(s).`);
            await loadGcalStatus();
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Échec de la synchronisation.");
        } finally {
            setGcalSyncing(false);
        }
    };

    // Charger l'état + gérer le retour OAuth (?google=connected|error)
    useEffect(() => {
        loadGcalStatus();
        const g = searchParams.get('google');
        if (g === 'connected') {
            toast.success("Google Agenda connecté ✅");
            const np = new URLSearchParams(searchParams); np.delete('google'); setSearchParams(np, { replace: true });
        } else if (g === 'error') {
            toast.error("La connexion Google Agenda a échoué. Réessayez.");
            const np = new URLSearchParams(searchParams); np.delete('google'); setSearchParams(np, { replace: true });
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Synchroniser vue/date/filtres avec l'URL (préservation du contexte au retour).
    useEffect(() => {
        const params = new URLSearchParams();
        if (view !== 'month') params.set('view', view);
        params.set('date', dateToYMD(curDate));
        if (filterScope !== 'mine') params.set('scope', filterScope);
        if (filterMember) params.set('member', filterMember);
        if (!showTasks) params.set('tasks', '0');
        if (!showEvents) params.set('events', '0');
        if (themeFilter !== 'all') params.set('theme', themeFilter);
        syncSearchParams(setSearchParams, searchParams, params);
    }, [view, curDate, filterScope, filterMember, showTasks, showEvents]); // eslint-disable-line react-hooks/exhaustive-deps
    
    // Form state
    const [form, setForm] = useState({
        title: '',
        description: '',
        startDate: '',
        startTime: '09:00',
        endDate: '',
        endTime: '10:00',
        all_day: false,
        location: '',
        recurrence: 'none',
        prospect_id: '',
        module: 'easy_power',
        category: 'dossier',
        theme: '',
        assigned_to: '',
        participant_ids: [],  // Multi-participants
        is_team_event: false,
        status: 'confirmed',
        color: '#3B82F6'
    });
    
    const isAdm = user?.role === 'admin_full' || user?.role === 'admin' || user?.is_admin === true;
    const isVP = user?.role === 'vp';
    const isApporteur = user?.role === 'apporteur';
    
    const filteredProspects = useMemo(() => {
        if (!prospectSearch) return prospects.slice(0, 15);
        const search = prospectSearch.toLowerCase();
        return prospects.filter(p => 
            (p.company_name?.toLowerCase().includes(search)) ||
            (p.first_name?.toLowerCase().includes(search)) ||
            (p.last_name?.toLowerCase().includes(search))
        ).slice(0, 15);
    }, [prospects, prospectSearch]);
    
    const selectedProspect = useMemo(() => {
        if (!form.prospect_id) return null;
        return prospects.find(p => p.id === form.prospect_id);
    }, [form.prospect_id, prospects]);
    
    const load = useCallback(async () => {
        const y = curDate.getFullYear();
        const m = curDate.getMonth() + 1;
        const sd = `${y}-${String(m).padStart(2, '0')}-01`;
        const ed = `${y}-${String(m).padStart(2, '0')}-31`;

        const params = { start_date: sd, end_date: ed };
        const taskParams = {};

        // Par défaut: voir ses propres RDV (admin et VP)
        if (isAdm) {
            if (filterScope === 'mine') { params.assigned_to = user.id; taskParams.assigned_to = user.id; }
        } else if (isVP) {
            if (filterScope === 'mine') { params.assigned_to = user.id; taskParams.assigned_to = user.id; }
            else if (filterScope === 'team') {
                params.team_scope = 'true'; taskParams.team_scope = 'true';
                if (filterMember) {
                    params.assigned_to = filterMember; taskParams.assigned_to = filterMember;
                    delete params.team_scope; delete taskParams.team_scope;
                }
            }
        }

        const key = `calendar:${user?.id}:${user?.role}:${y}-${m}:${filterScope}:${filterMember}`;
        const cached = getCached(key);
        if (cached) {
            setEvts(cached.events); setTasks(cached.tasks); setProspects(cached.prospects);
            setUsers(cached.users); if (cached.teamMembers) setTeamMembers(cached.teamMembers);
            setLoading(false);
        } else {
            setLoading(true);
        }
        try {
            const data = await swrFetch(key, async () => {
                const [res, tasksRes, pRes, usersRes, vpTeamRes] = await Promise.all([
                    calendarAPI.getEvents(params),
                    tasksAPI.getAll(taskParams),
                    prospectsAPI.getAll({ limit: 200 }),
                    usersAPI.getAll().catch(() => ({ data: [user] })),
                    (isVP ? usersAPI.getVPTeam(user.id).catch(() => ({ data: [] })) : Promise.resolve(null)),
                ]);
                return {
                    events: res.data || [],
                    tasks: tasksRes.data || [],
                    prospects: pRes.data || [],
                    users: usersRes.data || [user],
                    teamMembers: vpTeamRes ? (vpTeamRes.data || []) : undefined,
                };
            });
            setEvts(data.events); setTasks(data.tasks); setProspects(data.prospects);
            setUsers(data.users); if (data.teamMembers) setTeamMembers(data.teamMembers);
        } catch (e) {
            if (!cached) toast.error('Erreur chargement');
        } finally {
            setLoading(false);
        }
    }, [curDate, isAdm, isVP, user, filterScope, filterMember]);
    
    useEffect(() => { load(); }, [load]);
    
    const openNewEvent = (selectedDate = null) => {
        const date = selectedDate ? dateToYMD(selectedDate) : dateToYMD(new Date());
        const now = new Date();
        const nextHour = Math.min(now.getHours() + 1, 22);
        const startTime = `${String(nextHour).padStart(2, '0')}:00`;
        const endTime = `${String(nextHour + 1).padStart(2, '0')}:00`;
        
        setForm({
            title: '',
            description: '',
            startDate: date,
            startTime: startTime,
            endDate: date,
            endTime: endTime,
            all_day: false,
            location: '',
            recurrence: 'none',
            prospect_id: '',
            module: 'easy_power',
            category: 'dossier',
            theme: '',
            assigned_to: '',
            participant_ids: [],
            is_team_event: false,
            status: 'confirmed',
            color: '#3B82F6'
        });
        setFormMonth(selectedDate || new Date());
        setEditing(null);
        setShowOptions(false);
        setDlgOpen(true);
    };
    
    const submit = async (e) => {
        e.preventDefault();
        
        if (!form.title?.trim()) {
            toast.error('Le titre est requis');
            return;
        }
        if (!form.startDate) {
            toast.error('La date de début est requise');
            return;
        }
        if (form.category === 'dossier') {
            if (!form.prospect_id) {
                toast.error('Un événement « dossier » doit être lié à un prospect');
                return;
            }
            if (!form.module) {
                toast.error('Le module (Easy Power ou Chantier) est obligatoire');
                return;
            }
        } else if (!form.theme) {
            toast.error('Choisissez un thème pour l\'événement interne');
            return;
        }
        
        const start_datetime = form.all_day 
            ? `${form.startDate}T00:00:00`
            : combineDateAndTime(form.startDate, form.startTime);
        
        const end_datetime = form.all_day
            ? `${form.endDate || form.startDate}T23:59:59`
            : combineDateAndTime(form.endDate || form.startDate, form.endTime);
        
        setSaving(true);
        try {
            const participantIds = Array.isArray(form.participant_ids) ? [...form.participant_ids] : [];
            // Si pas de participants : on retombe sur assigned_to (legacy / single)
            const finalAssignedTo = form.assigned_to || (participantIds.length > 0 ? participantIds[0] : null);
            // Le primary est inclus dans participant_ids aussi pour la cohérence
            if (finalAssignedTo && !participantIds.includes(finalAssignedTo)) {
                participantIds.unshift(finalAssignedTo);
            }
            const payload = {
                title: form.title.trim(),
                description: (form.description || '') + (form.comment ? `\n\n--- Commentaire ---\n${form.comment}` : ''),
                start_datetime,
                end_datetime,
                all_day: form.all_day,
                location: form.location || '',
                recurrence: form.recurrence || 'none',
                prospect_id: form.prospect_id || null,
                module: form.module || null,
                category: form.category || 'dossier',
                theme: form.category === 'interne' ? (form.theme || null) : null,
                assigned_to: finalAssignedTo,
                participant_ids: participantIds,
                is_team_event: form.is_team_event || false,
                status: form.status || 'confirmed',
                color: form.color || '#3B82F6'
            };
            
            if (editing) {
                await calendarAPI.updateEvent(editing.id, payload);
                toast.success('Événement modifié');
            } else {
                await calendarAPI.createEvent(payload);
                toast.success('Événement créé');
            }
            setDlgOpen(false);
            setEditing(null);
            invalidatePrefix('calendar'); invalidatePrefix('tasks');
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur lors de la sauvegarde');
        } finally {
            setSaving(false);
        }
    };
    
    const del = async (id) => {
        if (!window.confirm('Supprimer cet événement ?')) return;
        try {
            await calendarAPI.deleteEvent(id);
            toast.success('Événement supprimé');
            setSelected(null);
            invalidatePrefix('calendar'); invalidatePrefix('tasks');
            load();
        } catch (e) {
            toast.error('Erreur suppression');
        }
    };
    
    const openEdit = (evt) => {
        setEditing(evt);
        setForm({
            title: evt.title || '',
            description: evt.description || '',
            startDate: extractDate(evt.start_datetime),
            startTime: extractTime(evt.start_datetime),
            endDate: extractDate(evt.end_datetime),
            endTime: extractTime(evt.end_datetime),
            all_day: evt.all_day || false,
            location: evt.location || '',
            recurrence: evt.recurrence || 'none',
            prospect_id: evt.prospect_id || '',
            module: evt.module || 'easy_power',
            category: evt.category || 'dossier',
            theme: evt.theme || '',
            assigned_to: evt.assigned_to || '',
            participant_ids: evt.participant_ids || (evt.assigned_to ? [evt.assigned_to] : []),
            is_team_event: evt.is_team_event || false,
            status: evt.status || 'confirmed',
            color: evt.color || '#3B82F6'
        });
        setFormMonth(new Date(evt.start_datetime));
        setShowOptions(true);
        setDlgOpen(true);
        setSelected(null);
    };
    
    const handleStartDateSelect = (date) => {
        setForm(f => ({
            ...f,
            startDate: date,
            endDate: f.endDate < date ? date : (f.endDate || date)
        }));
    };
    
    const handleStartTimeChange = (time) => {
        setForm(f => ({
            ...f,
            startTime: time,
            endTime: addHoursToTime(time, 1)
        }));
    };
    
    const grid = buildMonthGrid(curDate.getFullYear(), curDate.getMonth());
    const today = new Date();

    // Filtre par nature/thème : 'all' | 'dossier' | 'interne' | <thème précis>
    const matchTheme = (it) => {
        if (themeFilter === 'all') return true;
        const cat = it.category || 'dossier';
        if (themeFilter === 'dossier') return cat === 'dossier';
        if (themeFilter === 'interne') return cat === 'interne';
        return it.theme === themeFilter;
    };

    const evtsFor = (dt) => {
        const ymd = dateToYMD(dt);
        return showEvents ? evts.filter(e => e.start_datetime?.startsWith(ymd) && matchTheme(e)) : [];
    };
    
    const tasksFor = (dt) => {
        const ymd = dateToYMD(dt);
        return showTasks ? tasks.filter(t => t.due_date?.startsWith(ymd) && matchTheme(t)) : [];
    };
    
    const toggleTaskComplete = async (task) => {
        try {
            const newStatus = task.status === 'done' ? 'todo' : 'done';
            await tasksAPI.update(task.id, { status: newStatus });
            toast.success(newStatus === 'done' ? 'Tâche terminée' : 'Tâche rouverte');
            invalidatePrefix('calendar'); invalidatePrefix('tasks');
            load();
        } catch (e) {
            toast.error('Erreur');
        }
    };
    
    if (loading) {
        return (
            <DashboardLayout>
                <div className="flex items-center justify-center h-64">
                    <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                </div>
            </DashboardLayout>
        );
    }
    
    return (
        <DashboardLayout>
            <div className="space-y-6" data-testid="calendar-page">
                {/* Header */}
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900">Calendrier</h1>
                        <p className="text-slate-500 mt-1">Gérez vos rendez-vous et événements</p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        {/* Google Agenda */}
                        {!gcalState.loading && (gcalState.connected ? (
                            <div className="flex items-center gap-2" data-testid="gcal-connected-block">
                                <Button variant="outline" size="sm" onClick={handleGcalSync} disabled={gcalSyncing} data-testid="gcal-sync-btn">
                                    {gcalSyncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                                    {gcalSyncing ? 'Synchro…' : 'Synchroniser Google'}
                                </Button>
                                <Button variant="ghost" size="sm" onClick={handleGcalDisconnect} className="text-slate-500" data-testid="gcal-disconnect-btn" title={gcalState.email ? `Connecté : ${gcalState.email}` : 'Déconnecter'}>
                                    <span className="inline-flex items-center gap-1.5 text-emerald-600 text-xs font-medium">
                                        <span className="w-2 h-2 rounded-full bg-emerald-500" /> Google connecté
                                    </span>
                                </Button>
                            </div>
                        ) : (
                            <div className="flex flex-col items-end gap-1" data-testid="gcal-disconnected-block">
                                <Button variant="outline" size="sm" onClick={handleGcalConnect} data-testid="gcal-connect-btn">
                                    <CalendarIcon className="w-4 h-4 mr-2 text-[#4285F4]" />
                                    Connecter Google Agenda
                                </Button>
                                <p className="text-[11px] text-slate-400 max-w-[260px] text-right leading-tight" data-testid="gcal-connect-hint">
                                    Utilisez un compte <span className="font-medium">Gmail</span> ou votre adresse <span className="font-medium">@valnexo.fr</span> (à privilégier).
                                </p>
                            </div>
                        ))}
                        <Button className="bg-slate-900 hover:bg-slate-800" onClick={() => openNewEvent()} data-testid="new-event-btn">
                            <Plus className="w-4 h-4 mr-2" />
                            Nouvel Événement
                        </Button>
                    </div>
                </div>
                
                {/* Controls */}
                <Card>
                    <CardContent className="pt-4">
                        <div className="flex flex-col lg:flex-row items-center justify-between gap-4">
                            <div className="flex items-center gap-2">
                                <Button variant="outline" size="icon" onClick={() => setCurDate(new Date(curDate.getFullYear(), curDate.getMonth() - 1, 1))}>
                                    <ChevronLeft className="w-4 h-4" />
                                </Button>
                                <Button variant="outline" onClick={() => setCurDate(new Date())}>Aujourd'hui</Button>
                                <Button variant="outline" size="icon" onClick={() => setCurDate(new Date(curDate.getFullYear(), curDate.getMonth() + 1, 1))}>
                                    <ChevronRight className="w-4 h-4" />
                                </Button>
                                <h2 className="text-lg font-semibold ml-4 capitalize">{getMonthLabel(curDate)}</h2>
                            </div>
                            <div className="flex items-center gap-4 flex-wrap">
                                {/* Admin filter */}
                                {isAdm && (
                                    <Select value={filterScope} onValueChange={setFilterScope}>
                                        <SelectTrigger className="w-36" data-testid="calendar-filter-scope">
                                            <Users className="w-4 h-4 mr-2" />
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="mine">Mes RDV</SelectItem>
                                            <SelectItem value="all">Tous les RDV</SelectItem>
                                        </SelectContent>
                                    </Select>
                                )}
                                
                                {/* VP filters */}
                                {isVP && (
                                    <>
                                        <Select value={filterScope} onValueChange={(v) => { setFilterScope(v); setFilterMember(''); }}>
                                            <SelectTrigger className="w-40" data-testid="calendar-filter-scope-vp">
                                                <UsersRound className="w-4 h-4 mr-2" />
                                                <SelectValue placeholder="Périmètre" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="mine">Mes RDV</SelectItem>
                                                <SelectItem value="team">Mon équipe</SelectItem>
                                            </SelectContent>
                                        </Select>
                                        
                                        {filterScope === 'team' && teamMembers.length > 0 && (
                                            <Select value={filterMember} onValueChange={setFilterMember}>
                                                <SelectTrigger className="w-44">
                                                    <SelectValue placeholder="Tous" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="">Tous les membres</SelectItem>
                                                    {teamMembers.map(m => (
                                                        <SelectItem key={m.id} value={m.id}>{m.first_name} {m.last_name}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        )}
                                    </>
                                )}
                                
                                {/* Toggle Events/Tasks */}
                                <div className="flex items-center gap-2 text-sm">
                                    <button onClick={() => setShowEvents(!showEvents)} className={cn("flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-colors", showEvents ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-400")}>
                                        <CalendarIcon className="w-3.5 h-3.5" />
                                        Événements
                                    </button>
                                    <button onClick={() => setShowTasks(!showTasks)} className={cn("flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-colors", showTasks ? "bg-violet-100 text-violet-700" : "bg-slate-100 text-slate-400")}>
                                        <CheckSquare className="w-3.5 h-3.5" />
                                        Tâches
                                    </button>
                                </div>

                                {/* Filtre Nature / Thème (Commercial vs Interne/organisationnel) */}
                                <Select value={themeFilter} onValueChange={setThemeFilter}>
                                    <SelectTrigger className="w-44" data-testid="calendar-filter-theme">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Tout afficher</SelectItem>
                                        <SelectItem value="dossier">🏢 Commercial (dossiers)</SelectItem>
                                        <SelectItem value="interne">🗂️ Interne (tous)</SelectItem>
                                        {INTERNAL_THEMES.map(t => (
                                            <SelectItem key={t} value={t}>{t}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                
                                <Tabs value={view} onValueChange={setView}>
                                    <TabsList>
                                        <TabsTrigger value="month" className="gap-1"><CalendarIcon className="w-4 h-4" />Mois</TabsTrigger>
                                        <TabsTrigger value="list" className="gap-1"><List className="w-4 h-4" />Liste</TabsTrigger>
                                    </TabsList>
                                </Tabs>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                
                {/* Calendar Grid */}
                {view === 'month' && (
                    <Card>
                        <CardContent className="p-0">
                            <div className="grid grid-cols-7 border-b">
                                {WEEKDAYS.map(d => (
                                    <div key={d} className="p-1.5 sm:p-3 text-center text-[10px] sm:text-sm font-medium text-slate-500 border-r last:border-r-0">{d}</div>
                                ))}
                            </div>
                            <div className="grid grid-cols-7">
                                {grid.map((g, i) => {
                                    const dayEvts = evtsFor(g.dt);
                                    const dayTasks = tasksFor(g.dt);
                                    const totalItems = dayEvts.length + dayTasks.length;
                                    const isToday = sameDay(g.dt, today);
                                    return (
                                        <div 
                                            key={i} 
                                            className={cn(
                                                "min-h-[110px] p-2 border-r border-b cursor-pointer hover:bg-blue-50/50 transition-colors",
                                                !g.cur && "bg-slate-50/50",
                                                isToday && "bg-blue-50"
                                            )}
                                            onClick={() => openNewEvent(g.dt)}
                                        >
                                            <div className={cn(
                                                "text-sm font-medium mb-1 w-7 h-7 flex items-center justify-center rounded-full",
                                                !g.cur && "text-slate-400",
                                                isToday && "bg-blue-600 text-white"
                                            )}>
                                                {g.day}
                                            </div>
                                            <div className="space-y-1">
                                                {dayEvts.slice(0, 2).map(ev => (
                                                    <div key={ev.id} className="text-xs px-2 py-1 rounded text-white truncate flex items-center gap-1 hover:opacity-80" style={{backgroundColor: ev.color || '#3B82F6'}} onClick={e => {e.stopPropagation(); setSelected(ev);}}>
                                                        {!ev.all_day && <span className="opacity-75">{formatTime(ev.start_datetime)}</span>}
                                                        <span className="truncate">{ev.title}</span>
                                                    </div>
                                                ))}
                                                {dayTasks.slice(0, Math.max(0, 2 - dayEvts.length)).map(task => (
                                                    <div key={task.id} className={cn("text-xs px-2 py-1 rounded truncate flex items-center gap-1", task.status === 'done' ? "bg-slate-200 text-slate-500 line-through" : "text-white")} style={{backgroundColor: task.status === 'done' ? undefined : (TASK_PRIORITY_COLORS[task.priority] || '#8B5CF6')}} onClick={e => {e.stopPropagation(); setSelectedTask(task);}}>
                                                        {task.status === 'done' ? <CheckSquare className="w-3 h-3 shrink-0" /> : <Square className="w-3 h-3 shrink-0" />}
                                                        {task.title}
                                                    </div>
                                                ))}
                                                {totalItems > 2 && <div className="text-xs text-slate-500 font-medium">+{totalItems - 2} autres</div>}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </CardContent>
                    </Card>
                )}
                
                {/* List View */}
                {view === 'list' && (
                    <Card>
                        <CardContent className="pt-4">
                            {evts.length === 0 ? (
                                <div className="text-center py-12 text-slate-500">
                                    <CalendarIcon className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                                    <p>Aucun événement ce mois-ci</p>
                                </div>
                            ) : (
                                <div className="divide-y">
                                    {evts.map(ev => (
                                        <div key={ev.id} className="py-4 flex items-start gap-4 hover:bg-slate-50 cursor-pointer px-2 rounded" onClick={() => setSelected(ev)}>
                                            <div className="w-1 h-full min-h-[40px] rounded" style={{backgroundColor: ev.color || '#3B82F6'}} />
                                            <div className="flex-1">
                                                <h4 className="font-medium">{ev.title}</h4>
                                                <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                                                    <span className="flex items-center gap-1">
                                                        <Clock className="w-3.5 h-3.5" />
                                                        {formatDateFr(ev.start_datetime)} {!ev.all_day && `à ${formatTime(ev.start_datetime)}`}
                                                    </span>
                                                    {ev.location && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{ev.location}</span>}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                )}
                
                {/* ============ GOOGLE AGENDA STYLE FORM ============ */}
                <Dialog open={dlgOpen} onOpenChange={(o) => { setDlgOpen(o); if (!o) setEditing(null); }}>
                    <DialogContent className="max-w-xl p-0 overflow-hidden max-h-[90vh] flex flex-col">
                        <form onSubmit={submit} className="flex-1 overflow-y-auto">
                            {/* Header */}
                            <div className="flex items-center justify-between px-4 py-3 border-b bg-slate-50">
                                <button type="button" onClick={() => setDlgOpen(false)} className="text-blue-600 font-medium">Annuler</button>
                                <span className="font-semibold">{editing ? 'Modifier' : 'Ajouter un titre'}</span>
                                <button type="submit" disabled={saving} className="text-blue-600 font-medium disabled:opacity-50">
                                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enreg.'}
                                </button>
                            </div>
                            
                            {/* Title input */}
                            <div className="px-4 py-3 border-b">
                                <Input 
                                    value={form.title} 
                                    onChange={e => setForm(f => ({...f, title: e.target.value}))} 
                                    placeholder="Ajouter un titre"
                                    className="text-lg border-0 px-0 focus-visible:ring-0 placeholder:text-slate-400"
                                    data-testid="event-title-input"
                                    autoFocus
                                />
                            </div>
                            
                            {/* All day toggle */}
                            <div className="flex items-center justify-between px-4 py-3 border-b">
                                <div className="flex items-center gap-3">
                                    <Clock className="w-5 h-5 text-slate-500" />
                                    <span>Toute la journée</span>
                                </div>
                                <Switch checked={form.all_day} onCheckedChange={c => setForm(f => ({...f, all_day: c}))} />
                            </div>
                            
                            {/* Start date & time */}
                            <div className="px-4 py-2 border-b">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-slate-700 capitalize">{formatDateShort(form.startDate)}</span>
                                    {!form.all_day && (
                                        <Select value={form.startTime} onValueChange={handleStartTimeChange}>
                                            <SelectTrigger className="w-24 h-8 border-0 bg-transparent">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent className="max-h-[200px]">
                                                {TIME_SLOTS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    )}
                                </div>
                                
                                {/* Mini calendar */}
                                <MiniCalendar 
                                    selectedDate={form.startDate} 
                                    onSelect={handleStartDateSelect}
                                    month={formMonth}
                                    onMonthChange={setFormMonth}
                                />
                            </div>
                            
                            {/* End date & time */}
                            <div className="flex items-center justify-between px-4 py-3 border-b">
                                <span className="text-slate-700 capitalize">{formatDateShort(form.endDate || form.startDate)}</span>
                                {!form.all_day && (
                                    <Select value={form.endTime} onValueChange={v => setForm(f => ({...f, endTime: v}))}>
                                        <SelectTrigger className="w-24 h-8 border-0 bg-transparent">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent className="max-h-[200px]">
                                            {TIME_SLOTS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                )}
                            </div>
                            
                            {/* Nature + Prospect + Module (ou Thème si interne) */}
                            <div className="px-4 py-3 space-y-3 border-b bg-blue-50/30">
                                <div className="grid grid-cols-2 gap-2">
                                    <button type="button" onClick={() => setForm(f => ({ ...f, category: 'dossier' }))}
                                        data-testid="event-category-dossier"
                                        className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${form.category === 'dossier' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-600 hover:bg-white'}`}>
                                        🏢 Dossier
                                    </button>
                                    <button type="button" onClick={() => setForm(f => ({ ...f, category: 'interne' }))}
                                        data-testid="event-category-interne"
                                        className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${form.category === 'interne' ? 'border-violet-500 bg-violet-50 text-violet-700' : 'border-slate-200 text-slate-600 hover:bg-white'}`}>
                                        🗂️ Interne
                                    </button>
                                </div>
                                {form.category === 'dossier' ? (
                                  <>
                                    {prospects.length > 0 && (
                                    <div className="flex items-center gap-3">
                                        <Building2 className="w-5 h-5 text-slate-500" />
                                        <Popover open={prospectPopoverOpen} onOpenChange={setProspectPopoverOpen}>
                                            <PopoverTrigger asChild>
                                                <button type="button" className="flex-1 text-left text-sm" data-testid="event-prospect-trigger">
                                                    {selectedProspect ? (
                                                        <span>{selectedProspect.company_name || `${selectedProspect.first_name} ${selectedProspect.last_name}`}</span>
                                                    ) : (
                                                        <span className="text-slate-400">Lier à un prospect <span className="text-red-500">*</span></span>
                                                    )}
                                                </button>
                                            </PopoverTrigger>
                                            <PopoverContent className="w-[300px] p-0" align="start">
                                                <Command shouldFilter={false}>
                                                    <CommandInput placeholder="Rechercher..." value={prospectSearch} onValueChange={setProspectSearch} />
                                                    <CommandList>
                                                        <CommandEmpty>Aucun prospect</CommandEmpty>
                                                        <CommandGroup>
                                                            {filteredProspects.map(p => (
                                                                <CommandItem key={p.id} onSelect={() => { setForm(f => ({...f, prospect_id: p.id})); setProspectPopoverOpen(false); }}>
                                                                    <Check className={cn("mr-2 h-4 w-4", form.prospect_id === p.id ? "opacity-100" : "opacity-0")} />
                                                                    {p.company_name || `${p.first_name} ${p.last_name}`}
                                                                </CommandItem>
                                                            ))}
                                                        </CommandGroup>
                                                    </CommandList>
                                                </Command>
                                            </PopoverContent>
                                        </Popover>
                                    </div>
                                    )}
                                    <div className="space-y-1.5" data-testid="event-module-block">
                                        <Label className="text-xs text-slate-500">Mur du dossier concerné <span className="text-red-500">*</span></Label>
                                        <Select value={form.module} onValueChange={(v) => setForm(f => ({...f, module: v}))}>
                                            <SelectTrigger data-testid="event-module-select">
                                                <SelectValue placeholder="Choisir un module" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="easy_power">Easy Power</SelectItem>
                                                <SelectItem value="chantier">Chantier</SelectItem>
                                            </SelectContent>
                                        </Select>
                                        <p className="text-[11px] text-slate-400">L'événement apparaîtra sur le mur de ce module.</p>
                                    </div>
                                  </>
                                ) : (
                                    <div className="space-y-1.5">
                                        <Label className="text-xs text-slate-500">Thème <span className="text-red-500">*</span></Label>
                                        <Select value={form.theme || ''} onValueChange={(v) => setForm(f => ({...f, theme: v}))}>
                                            <SelectTrigger data-testid="event-theme-select">
                                                <SelectValue placeholder="Choisir un thème" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {INTERNAL_THEMES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                        <p className="text-[11px] text-slate-400">Événement organisationnel, non lié à un dossier.</p>
                                    </div>
                                )}
                            </div>

                            {/* Other options (collapsible) */}
                            <Collapsible open={showOptions} onOpenChange={setShowOptions}>
                                <CollapsibleTrigger className="flex items-center justify-between w-full px-4 py-3 border-b hover:bg-slate-50">
                                    <span className="text-slate-600">Autres options</span>
                                    <ChevronDown className={cn("w-5 h-5 text-slate-400 transition-transform", showOptions && "rotate-180")} />
                                </CollapsibleTrigger>
                                <CollapsibleContent>
                                    <div className="px-4 py-3 space-y-4 border-b bg-slate-50/50">
                                        {/* Location */}
                                        <div className="flex items-center gap-3">
                                            <MapPin className="w-5 h-5 text-slate-500" />
                                            <Input 
                                                value={form.location} 
                                                onChange={e => setForm(f => ({...f, location: e.target.value}))} 
                                                placeholder="Ajouter un lieu"
                                                className="border-0 bg-transparent px-0 focus-visible:ring-0"
                                            />
                                        </div>
                                        
                                        {/* Team event for VP */}
                                        {isVP && (
                                            <div className="flex items-center gap-3">
                                                <UsersRound className="w-5 h-5 text-violet-500" />
                                                <label className="flex items-center gap-2 cursor-pointer">
                                                    <input 
                                                        type="checkbox"
                                                        checked={form.is_team_event}
                                                        onChange={e => setForm(f => ({...f, is_team_event: e.target.checked}))}
                                                        className="w-4 h-4 rounded"
                                                        data-testid="team-event-checkbox"
                                                    />
                                                    <span className="text-sm">Événement d'équipe</span>
                                                </label>
                                            </div>
                                        )}
                                        
                                        {/* Assigner des participants - multi-sélection */}
                                        <div className="flex items-start gap-3">
                                            <UserPlus className="w-5 h-5 text-slate-500 mt-2" />
                                            <div className="flex-1 space-y-1">
                                                <div className="text-xs text-slate-500">Participants <span className="text-slate-400">(plusieurs possible)</span></div>
                                                <div className="border border-slate-200 rounded-md max-h-64 overflow-y-auto bg-white" data-testid="event-participants-list">
                                                    <label className="flex items-center gap-2 px-3 py-1.5 hover:bg-slate-50 cursor-pointer border-b border-slate-100">
                                                        <input
                                                            type="checkbox"
                                                            className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500"
                                                            checked={form.participant_ids.includes(user?.id)}
                                                            onChange={(e) => {
                                                                setForm(prev => ({
                                                                    ...prev,
                                                                    participant_ids: e.target.checked
                                                                        ? Array.from(new Set([...(prev.participant_ids || []), user.id]))
                                                                        : (prev.participant_ids || []).filter(id => id !== user?.id)
                                                                }));
                                                            }}
                                                            data-testid="event-participant-me"
                                                        />
                                                        <span className="text-sm">Moi-même</span>
                                                    </label>
                                                    {users.filter(u => {
                                                        if (u.id === user?.id || u.is_active === false) return false;
                                                        if (isAdm) return true;
                                                        if (isVP) return u.role === 'admin_full' || u.role === 'admin' || (u.vp_id === user?.id);
                                                        if (user?.role === 'apporteur') return u.role === 'admin_full' || u.role === 'admin' || u.id === user?.vp_id || (u.role === 'apporteur' && u.vp_id === user?.vp_id);
                                                        return false;
                                                    }).map(u => (
                                                        <label key={u.id} className="flex items-center gap-2 px-3 py-1.5 hover:bg-slate-50 cursor-pointer border-b border-slate-100 last:border-0">
                                                            <input
                                                                type="checkbox"
                                                                className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500"
                                                                checked={form.participant_ids.includes(u.id)}
                                                                onChange={(e) => {
                                                                    setForm(prev => ({
                                                                        ...prev,
                                                                        participant_ids: e.target.checked
                                                                            ? Array.from(new Set([...(prev.participant_ids || []), u.id]))
                                                                            : (prev.participant_ids || []).filter(id => id !== u.id)
                                                                    }));
                                                                }}
                                                                data-testid={`event-participant-${u.id}`}
                                                            />
                                                            <span className="text-sm">{u.first_name} {u.last_name}</span>
                                                            <span className="text-xs text-slate-400 ml-1">({u.role === 'admin_full' || u.role === 'admin' ? 'Admin' : u.role === 'vp' ? 'VP' : 'Apporteur'})</span>
                                                        </label>
                                                    ))}
                                                </div>
                                                {form.participant_ids.length > 0 && (
                                                    <div className="text-xs text-blue-600">{form.participant_ids.length} participant{form.participant_ids.length > 1 ? 's' : ''}</div>
                                                )}
                                            </div>
                                        </div>
                                        
                                        {/* Color */}
                                        <div className="flex items-center gap-3">
                                            <div className="w-5 h-5 rounded-full" style={{backgroundColor: form.color}} />
                                            <div className="flex gap-1.5">
                                                {COLORS.map(c => (
                                                    <button 
                                                        key={c.value} 
                                                        type="button" 
                                                        className={cn("w-6 h-6 rounded-full border-2 transition-transform", form.color === c.value ? "border-slate-900 scale-110" : "border-transparent")} 
                                                        style={{backgroundColor: c.value}} 
                                                        onClick={() => setForm(f => ({...f, color: c.value}))}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                        
                                        {/* Description */}
                                        <Textarea 
                                            value={form.description} 
                                            onChange={e => setForm(f => ({...f, description: e.target.value}))} 
                                            placeholder="Ajouter une description"
                                            rows={2}
                                            className="text-sm"
                                        />
                                        
                                        {/* Commentaire */}
                                        <Textarea 
                                            value={form.comment || ''} 
                                            onChange={e => setForm(f => ({...f, comment: e.target.value}))} 
                                            placeholder="Commentaire ou détails supplémentaires..."
                                            rows={2}
                                            className="text-sm bg-amber-50/50 border-amber-200"
                                        />
                                    </div>
                                </CollapsibleContent>
                            </Collapsible>
                        </form>
                    </DialogContent>
                </Dialog>
                
                {/* Event detail */}
                {selected && (
                    <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle className="flex items-center gap-2">
                                    <div className="w-3 h-3 rounded-full" style={{backgroundColor: selected.color || '#3B82F6'}} />
                                    {selected.title}
                                </DialogTitle>
                            </DialogHeader>
                            <div className="space-y-3">
                                <div className="flex items-center gap-2 text-slate-600">
                                    <Clock className="w-4 h-4" />
                                    <span>{formatDateFr(selected.start_datetime)} {!selected.all_day && `de ${formatTime(selected.start_datetime)} à ${formatTime(selected.end_datetime)}`}</span>
                                </div>
                                {selected.location && <div className="flex items-center gap-2 text-slate-600"><MapPin className="w-4 h-4" />{selected.location}</div>}
                                {selected.description && <p className="text-slate-600 bg-slate-50 p-3 rounded-lg">{selected.description}</p>}
                            </div>
                            <DialogFooter className="gap-2">
                                <Button variant="outline" onClick={() => openEdit(selected)}><Edit className="w-4 h-4 mr-2" />Modifier</Button>
                                <Button variant="destructive" onClick={() => del(selected.id)}><Trash2 className="w-4 h-4 mr-2" />Supprimer</Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                )}
                
                {/* Task detail */}
                {selectedTask && (
                    <Dialog open={!!selectedTask} onOpenChange={() => setSelectedTask(null)}>
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle>{selectedTask.title}</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-3">
                                {selectedTask.due_date && <div className="flex items-center gap-2 text-slate-600"><Clock className="w-4 h-4" />Échéance: {formatDateFr(selectedTask.due_date)}</div>}
                                {selectedTask.description && <p className="text-slate-600">{selectedTask.description}</p>}
                                <div className="flex gap-2">
                                    <Badge variant={selectedTask.priority === 'urgent' ? 'destructive' : 'secondary'}>{selectedTask.priority}</Badge>
                                    <Badge variant={selectedTask.status === 'done' ? 'outline' : 'default'}>{selectedTask.status === 'done' ? '✓ Terminée' : 'À faire'}</Badge>
                                </div>
                            </div>
                            <DialogFooter>
                                <Button variant={selectedTask.status === 'done' ? 'outline' : 'default'} onClick={() => { toggleTaskComplete(selectedTask); setSelectedTask(null); }} className={selectedTask.status !== 'done' ? 'bg-green-600 hover:bg-green-700' : ''}>
                                    {selectedTask.status === 'done' ? 'Rouvrir' : 'Terminer'}
                                </Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                )}
            </div>
        </DashboardLayout>
    );
}
